import { edgeInvoke } from "@/lib/edgeInvoke";
import { callEdge } from "@/lib/edgeClient";

export type CampaignSessionStartResponse = {
  ok: boolean;
  campaign_session_id?: string;
  campaign_session_token?: string;
  expires_at?: string;
  error?: string;
};

export type CampaignUploadUrlResponse = {
  ok: boolean;
  uploadURL?: string;
  uid?: string;
  error?: string;
};

export type CampaignRunCreateResponse = {
  ok: boolean;
  campaign_run_id?: string;
  swing_run_id?: string;
  alreadyConfirmed?: boolean;
  error?: string;
};

export type CampaignSwingCreateResponse = {
  ok: boolean;
  swing_id?: string;
  campaign_run_id?: string;
  upload?: {
    provider: "cloudflare";
    uid: string;
    signed_url: string;
    expires_at: string;
  };
  error?: string;
};

export type CampaignUpgradeResponse = {
  ok: boolean;
  campaign_session_id?: string;
  upgraded?: boolean;
  reassigned_runs?: number;
  alreadyUpgraded?: boolean;
  error?: string;
};

export type OtpSendResponse = {
  ok: boolean;
  expires_in_seconds?: number;
  error?: string;
};

export type OtpVerifyResponse = {
  ok: boolean;
  session_token?: string;
  refresh_token?: string;
  user_id?: string;
  already_verified?: boolean;
  error?: string;
};

/**
 * Start an anonymous campaign session.
 */
export async function startCampaignSession(params: {
  campaign_id?: string;
  source_metadata?: Record<string, unknown>;
}): Promise<CampaignSessionStartResponse> {
  return edgeInvoke<CampaignSessionStartResponse>("campaign-session-start", {
    campaign_id: params.campaign_id,
    source_metadata: params.source_metadata,
  });
}

/**
 * Get a Cloudflare direct upload URL for an anonymous campaign session.
 */
export async function getCampaignUploadUrl(params: {
  campaign_session_token: string;
  submitted_email?: string | null;
}): Promise<CampaignUploadUrlResponse> {
  return edgeInvoke<CampaignUploadUrlResponse>(
    "campaign-upload-url",
    { submitted_email: params.submitted_email ?? null },
    {
      headers: {
        Authorization: `Bearer ${params.campaign_session_token}`,
      },
    }
  );
}

/**
 * PLA-30: Create a campaign swing record and get a Cloudflare upload URL (pre-upload).
 */
export async function createCampaignSwing(params: {
  campaign_session_token: string;
  handedness?: string | null;
  club_type?: string | null;
  recording_context?: string | null;
  original_filename?: string | null;
  submitted_email?: string | null;
}): Promise<CampaignSwingCreateResponse> {
  return edgeInvoke<CampaignSwingCreateResponse>(
    "campaign-swings",
    {
      handedness: params.handedness ?? null,
      club_type: params.club_type ?? null,
      recording_context: params.recording_context ?? null,
      original_filename: params.original_filename ?? null,
      submitted_email: params.submitted_email ?? null,
    },
    {
      headers: {
        Authorization: `Bearer ${params.campaign_session_token}`,
      },
    }
  );
}

/**
 * Create a campaign run and kick off processing.
 */
export async function createCampaignRun(params: {
  campaign_session_token: string;
  uid: string;
  original_filename?: string | null;
}): Promise<CampaignRunCreateResponse> {
  return edgeInvoke<CampaignRunCreateResponse>(
    "campaign-run-create",
    {
      uid: params.uid,
      original_filename: params.original_filename ?? null,
    },
    {
      headers: {
        Authorization: `Bearer ${params.campaign_session_token}`,
      },
    }
  );
}

/**
 * Upgrade an anonymous campaign session to the currently authenticated user.
 *
 * Uses `callEdge` so we can attach the user JWT (authRequired=true) AND the campaign token header.
 */
export async function upgradeCampaignSession(params: {
  campaign_session_token: string;
}): Promise<CampaignUpgradeResponse> {
  return callEdge("campaign-upgrade", {
    method: "POST",
    authRequired: true,
    headers: {
      "x-campaign-token": params.campaign_session_token,
    },
    body: {},
  }) as Promise<CampaignUpgradeResponse>;
}

/**
 * PLA-33 (R01-06): Send OTP code to email associated with campaign session.
 */
export async function sendOtp(params: {
  campaign_session_token: string;
}): Promise<OtpSendResponse> {
  return edgeInvoke<OtpSendResponse>(
    "campaign-otp-send",
    {},
    {
      headers: {
        "x-campaign-token": params.campaign_session_token,
      },
    }
  );
}

/**
 * PLA-33 (R01-06): Verify OTP code and upgrade campaign session to authenticated user.
 */
export async function verifyOtp(params: {
  campaign_session_token: string;
  code: string;
}): Promise<OtpVerifyResponse> {
  return edgeInvoke<OtpVerifyResponse>(
    "campaign-otp-verify",
    {
      code: params.code,
    },
    {
      headers: {
        "x-campaign-token": params.campaign_session_token,
      },
    }
  );
}


